import { Tab } from "@headlessui/react";
import { useAtom } from "jotai";
import { useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { RateCardGrid } from "@/components/Checkout/Steps/RateSelection/RateCard/RateCardGrid";
import { RateFormProps } from "@/components/Checkout/Steps/RateSelection/RateSelection";
import {
  getDataOnlyProducts,
  getMobileAbos,
  getSortedMobileAbosMonthly,
} from "@/components/Checkout/Steps/RateSelection/rateSelection.utils";
import { RateTypeTabs } from "@/components/Checkout/Steps/RateSelection/RateTypeTabs/RateTypeTabs";
import { Button } from "@/components/Interface/Button/Button";
import { CardContainer } from "@/components/Layout/BaseLayouts/CardContainer";
import { StepFlowNavigation } from "@/components/Layout/BaseLayouts/StepFlowLayout/StepFlowNavigation";
import { LocalLoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import { transformOptionsToPreselection } from "@/components/Portal/DeviceManagement/SubscriptionDetails/SubscriptionChange/hooks/transformOptionsToPreslection";
import { useShowFlatMobileFirstFromQueryParam } from "@/hooks/useShowFlatMobileFirstFromQueryParam";
import { useSimIdFromQueryParam } from "@/hooks/useSimIdFromQueryParam";
import { portalRoutes } from "@/router/routes/portalRoutes";
import {
  getAvailableSunriseRoamingPackages,
  getSim,
  useGetCustomer,
  useGetCustomerTribe,
  useGetDetailTribe,
} from "@/services/api";
import {
  GetAvailableSunriseRoamingPackagesPackageType,
  SimCardDetail,
} from "@/services/model";
import {
  accessTokenAtom,
  portalTariffProductIDAtom,
  tariffCheckoutPaymentPersistenceAtom,
} from "@/utils/atoms";
import { canOnlyBookData } from "@/utils/customerUtils";
import { isInactive } from "@/utils/deviceUtils";
import {
  isAnyFlatMobileTariff,
  TransformedProducts,
  transformProducts,
} from "@/utils/tariffUtils";

/**
 * Step 1 of the subscription-change flow: rate selection.
 * Fetches available products for the sim and lets the user pick one.
 */
export const SubscriptionRateSelection = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [simId] = useSimIdFromQueryParam();
  const [showFlatMobileFirst] = useShowFlatMobileFirstFromQueryParam();
  const { data: customerData } = useGetCustomer();
  const [accessToken] = useAtom(accessTokenAtom);
  const { data: customerTribe } = useGetCustomerTribe();
  const { data: tribeData } = useGetDetailTribe(customerTribe?.tribeId ?? "", {
    query: { enabled: !!accessToken && !!customerTribe?.tribeId },
  });
  const tribeDiscount = tribeData?.tribeDiscountNext;

  const [simDetail, setSimDetail] = useState<SimCardDetail | undefined>();
  const [options, setOptions] = useState<TransformedProducts | undefined>();
  const [rateTypeTabIndex, setRateTypeTabIndex] = useState(0);

  const [portalTariffProductID, setPortalTariffProductID] = useAtom(
    portalTariffProductIDAtom,
  );
  const [, setTariffPaymentPersistence] = useAtom(
    tariffCheckoutPaymentPersistenceAtom,
  );

  const canShowSwitchToMobile = !customerData || !canOnlyBookData(customerData);

  const rateFormMethods = useForm<Omit<RateFormProps, "numberPorting">>({
    defaultValues: async () => {
      const sim = await getSim({ simId: parseInt(simId) });
      const packages = await getAvailableSunriseRoamingPackages(
        parseInt(simId),
        {
          packageType: GetAvailableSunriseRoamingPackagesPackageType.Data_CH,
        },
      ).then((res) => transformProducts(res?.subscriberPackageData));

      setSimDetail(sim);
      setOptions(packages);

      const shouldStartOnMobileTab =
        isAnyFlatMobileTariff(sim?.serviceID) ||
        (!!sim?.localFlatSpeed && sim.localFlatSpeed.includes("Flat Mobile")) ||
        showFlatMobileFirst === "true";

      setRateTypeTabIndex(shouldStartOnMobileTab ? 0 : 1);

      const { rate } = transformOptionsToPreselection(
        sim,
        packages,
        portalTariffProductID || sim?.serviceID,
      );

      return { rate: rate || "" };
    },
  });

  const selectedOption = Number(rateFormMethods.watch("rate"));

  const navigateForwards = () => {
    setPortalTariffProductID(selectedOption);
    setTariffPaymentPersistence(undefined);
    navigate(
      `${portalRoutes.manageDevice.subscription.checkout}?simId=${simDetail?.simId}`,
    );
  };

  const navigateBack = () => {
    setPortalTariffProductID(undefined);
    navigate(
      `${portalRoutes.manageDevice.subscription.display}?simId=${simDetail?.simId}`,
      { replace: true },
    );
  };

  const disableSuccessButton =
    (!isInactive(simDetail) &&
      simDetail?.autoRenew &&
      !!simDetail?.successorForRenew?.id &&
      selectedOption === simDetail?.successorForRenew?.id) ||
    !selectedOption;

  const mobileAboProducts = getMobileAbos(options?.processedProductsById);
  const monthlyMobileAbos = getSortedMobileAbosMonthly(mobileAboProducts);

  const dataOnlyProducts = useMemo(
    () => getDataOnlyProducts(options?.processedProductsById),
    [options?.processedProductsById],
  );

  // The product ID currently booked on this SIM — shown with "Aktuell" chip.
  const currentDataRateId = useMemo(() => {
    if (!simDetail || !options || isInactive(simDetail)) {
      return undefined;
    }

    const unavailableId = simDetail.autoRenew
      ? simDetail.successorForRenew?.id
      : simDetail.serviceID;

    if (!unavailableId) {
      return undefined;
    }

    const product = options.processedProductsById[unavailableId];

    // If it's a voice product, point to the data base product; otherwise use id directly.
    return product?.data !== undefined && product.data !== product.id
      ? product.data
      : unavailableId;
  }, [simDetail, options]);

  const currentTariffLabel = t("portal:subscription-change.currentTariffLabel");

  const isLoading = rateFormMethods.formState.isLoading;

  return (
    <div className="flex w-full flex-col gap-6">
      <FormProvider {...rateFormMethods}>
        <CardContainer
          className="flex w-full flex-col rounded-lg pb-2"
          variant="white"
        >
          {isLoading || !options ? (
            <div className="flex justify-center py-8">
              <LocalLoadingSpinner />
            </div>
          ) : (
            <RateTypeTabs
              selectedIndex={rateTypeTabIndex}
              onIndexChange={setRateTypeTabIndex}
              listClassName="px-4 pb-2 pt-4"
              hideSecondTab={!canShowSwitchToMobile}
              firstLabel={t(
                "Onboarding.sections.rate-selection.tabs.mobileAbos",
              )}
              secondLabel={t(
                "Onboarding.sections.rate-selection.tabs.dataOnly",
              )}
            >
              <Tab.Panels className="flex flex-col gap-6 px-4 py-2">
                <Tab.Panel>
                  <RateCardGrid
                    value={rateFormMethods.watch("rate")}
                    products={monthlyMobileAbos}
                    currentProductId={currentDataRateId}
                    currentLabel={currentTariffLabel}
                    tribeDiscount={tribeDiscount}
                    onChange={(rate) => rateFormMethods.setValue("rate", rate)}
                  />
                </Tab.Panel>
                <Tab.Panel>
                  <RateCardGrid
                    value={rateFormMethods.watch("rate")}
                    products={dataOnlyProducts}
                    hint={t("Onboarding.sections.rate-selection.tariffHint")}
                    currentProductId={currentDataRateId}
                    currentLabel={currentTariffLabel}
                    onChange={(rate) => rateFormMethods.setValue("rate", rate)}
                  />
                </Tab.Panel>
              </Tab.Panels>
            </RateTypeTabs>
          )}
        </CardContainer>
      </FormProvider>

      <StepFlowNavigation
        primaryButton={
          <Button
            dataTestid="success-button"
            variant="primary"
            disabled={disableSuccessButton}
            loading={isLoading}
            onClick={navigateForwards}
          >
            {t("common.buttons.continue")}
          </Button>
        }
        secondaryButton={
          <Button variant="secondary" onClick={navigateBack}>
            {t("common.buttons.cancel")}
          </Button>
        }
      />
    </div>
  );
};
